import React, {useRef, useEffect, useCallback, useReducer, useState} from "react";
import {Box, Flex, Skeleton, useDisclosure} from "@chakra-ui/react";
import useUserStore from "../store/useUserStore.ts";
import {useProjectsStore} from "../store/useProjectsStore.ts";
import FeedbackDrawer from "../components/FeedbackDrawer/FeedbackDrawer.tsx";
import DeleteProjectAlertDialog from "../components/projectsTable/DeleteProjectAlertDialog.tsx";
import {getRemainingMinutes} from "../services/userService.ts";
import {ONE_MINUTE_IN_ML} from "../models/constants/constants.ts";
import MainTableActions from "../components/projectsTable/MainTableActions.tsx";
import EmptyProjectTableView from "../components/projectsTable/EmptyProjectTableView.tsx";
import {IWorkspaceRemainingMinutes} from "../models/interfaces/workspaceRemainingMinutes.interface.ts";
import ProjectPreviewModal from "../components/CreateProjectModal/ProjectPreviewModal.tsx";
import {useProjectUploader} from "../hooks/useProjectUploader.ts";
import {useProjectPolling} from "../hooks/useProjectPolling";
import {usePagination} from "../hooks/usePagination.ts";
import ProjectTable from "../components/projectsTable/ProjectTable.tsx";
import PaginationControls from "../components/projectsTable/PaginationControls.tsx";
import UploadToaster from "../components/UploadToaster.tsx";
import {
  mainPageReducer,
  initialMainPageState,
} from "../reducers/mainPageReducer";
import {IProject} from "../models/interfaces/project.interface.ts";
import {useProject} from "../hooks/useProject.ts";
import {useCreateNewProjectModal} from "../hooks/useCreateNewProjectModal.tsx";
import {ICreateNewProjectState} from "../reducers/createNewProjectReducer.ts";
import useUiStore from "../store/useUiStore.ts";
import ProjectDrawer from "../components/ProjectDetails/ProjectDrawer.tsx";

const calculateItemsPerPage = () => {
  const screenHeight = window.innerHeight;

  if (screenHeight >= 1080) return 8;  // large external screen
  if (screenHeight >= 900) return 7;   // medium resolution
  return 6;                            // smaller laptops
};

const MainPage = () => {
  const {closeModal, openDrawer} = useUiStore();
  const {user, getLanguageConfig, getUserPreferences} = useUserStore();
  const {projects, projectsToView, getProjects, getProjectStatus} = useProjectsStore();
  const {calculateProjectPercentage} = useProject()
  const {uploads, triggerUpload} = useProjectUploader();
  useProjectPolling(projects, getProjectStatus, ONE_MINUTE_IN_ML);
  const [state, dispatch] = useReducer(mainPageReducer, initialMainPageState);
  const {
    isOpen: isAlertDialogOpen,
    onOpen: onAlertDialogOpen,
    onClose: onAlertDialogClose,
  } = useDisclosure();
  const [itemsPerPage, setItemsPerPage] = useState(calculateItemsPerPage());
  const tableRef = useRef<HTMLDivElement>(null);
  const showCreateNewProjectModal: (remainingMinutes: number, handleCreateProject: (state: ICreateNewProjectState) => Promise<void>) => void = useCreateNewProjectModal()

  const handleClosePreviewModal = useCallback(() => {
    dispatch({type: "SET_PREVIEW_MODAL", payload: false})
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(calculateItemsPerPage());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch projects initially
  useEffect(() => {
    getProjects().then(() => {
      dispatch({type: "SET_GET_PROJECT_TRIGGERED", payload: true});
    });
  }, [getProjects]);

  useEffect(() => {
    getLanguageConfig().then(() => null);
    getUserPreferences().then(() => null);
  }, [getLanguageConfig, getUserPreferences]);

  const getUserRemainingMinutes = async (): Promise<number> => {
    try {
      if (!user?.workspaceId) return 0;
      const remainingMinutes: IWorkspaceRemainingMinutes = await getRemainingMinutes(
        user.workspaceId
      );
      return remainingMinutes.remainingMinutes;
    } catch (error) {
      console.error("Error fetching remaining minutes:", error);
      return 0;
    }
  };

  const handleCreateProject = async (
    state: ICreateNewProjectState
  ): Promise<void> => {
    closeModal();
    triggerUpload(state).catch((error: Error) => console.error(error));
  };

  const onCreateNewProjectClicked = async (): Promise<void> => {
    const remainingMinutes: number = await getUserRemainingMinutes();
    showCreateNewProjectModal(remainingMinutes, handleCreateProject)
  };

  const openFeedbackDrawer = (projectId: string, targetLanguage: string[]) => {
    dispatch({type: "SET_FEEDBACK_PROJECT_ID", payload: projectId})
    dispatch({type: "SET_FEEDBACK_TARGET_LANGUAGES", payload: targetLanguage})
    dispatch({type: "SET_SHOW_FEEDBACK_DRAWER", payload: true})
  };

  const toggleSelectedRow = (project: IProject): void => {
    const selectedProjects = [...state.selectedRows];
    if (selectedProjects.includes(project.id)) {
      dispatch({
        type: "SET_SELECTED_ROWS",
        payload: selectedProjects.filter((projectId: string) => projectId !== project.id)
      })
    } else {
      dispatch({type: "SET_SELECTED_ROWS", payload: [...selectedProjects, project.id]})
    }
  };


  const selectAllCompletedProjects = (projects: IProject[]): void => {
    dispatch({
      type: "SET_SELECTED_ROWS",
      payload: projects
        .filter((project: IProject) => calculateProjectPercentage(project) === 100)
        .map((project: IProject) => project.id)
    })
  };

  const presentPreview = (projectId: string) => {
    dispatch({type: "SET_PREVIEW_PROJECT_ID", payload: projectId})
    dispatch({type: "SET_PREVIEW_MODAL", payload: true})
  };

  const {currentPage, setCurrentPage, totalPages, paginatedIndices} = usePagination(
    projects.length,
    itemsPerPage
  );

  const paginatedProjects: IProject[] = projects.filter((project: IProject) => {
    return projectsToView.length > 0 ? projectsToView.includes(project.id) : true;
  }).sort((a: IProject, b: IProject) => {
    const aDate = new Date(a.updatedAt);
    const bDate = new Date(b.updatedAt);
    return bDate.getTime() - aDate.getTime();
  }).slice(paginatedIndices.start, paginatedIndices.end);

  const openProjectDrawer = (projectId: string) => {
    openDrawer(<ProjectDrawer projectId={projectId}/>)
  }

  return (
    <>
      {/* Projects Table */}
      <Flex className="project-table-wrapper">
        <MainTableActions
          selectedRows={state.selectedRows}
          setSelectedRows={(selectedRows: string[]) => dispatch({type: "SET_SELECTED_ROWS", payload: selectedRows})}
          isDownloading={state.isDownloading}
          setIsDownloading={(update) =>
            dispatch({
              type: "SET_IS_DOWNLOADING",
              payload: typeof update === "function" ? update(state.isDownloading) : update,
            })
          }
          selectAllCompletedProjects={() => selectAllCompletedProjects(projects)}
          onCreateNewProjectClicked={onCreateNewProjectClicked}
        />
        <Skeleton isLoaded={state.isGetProjectTriggered} className="mt-4">
          <Box minH={`${itemsPerPage * 88}px`} className="w-full">
            {projects.length == 0
              ? <EmptyProjectTableView onCreateNewProjectClicked={onCreateNewProjectClicked}/>
              : (
                <>
                  <ProjectTable
                    tableRef={tableRef}
                    projects={paginatedProjects}
                    selectedRows={state.selectedRows}
                    toggleSelectedRow={toggleSelectedRow}
                    setSelectedProjectId={(id: string | null) => dispatch({
                      type: "SET_SELECTED_PROJECT_ID",
                      payload: id
                    })}
                    onAlertDialogOpen={onAlertDialogOpen}
                    showProjectOverview={(projectId: string) => openProjectDrawer(projectId)}
                    openFeedbackDrawer={openFeedbackDrawer}
                    presentPreview={presentPreview}
                    isDownloading={state.isDownloading}
                    setIsDownloading={(update) =>
                      dispatch({
                        type: "SET_IS_DOWNLOADING",
                        payload: typeof update === "function" ? update(state.isDownloading) : update,
                      })
                    }
                  />
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                    hoveredButton={state.hoveredButton}
                    setHoveredButton={(hovered: string | null) => dispatch({
                      type: "SET_HOVERED_BUTTON",
                      payload: hovered
                    })}
                  />
                </>
              )
            }
          </Box>
        </Skeleton>
      </Flex>

      <FeedbackDrawer
        projectId={state.feedbackProjectId}
        targetLanguages={state.feedbackTargetLanguages}
        maxRowNumber={
          state.feedbackProjectId
            ? projects.find((p: IProject) => p.id === state.feedbackProjectId)!.mediaMeta.amountRows
            : 0
        }
        isOpen={state.isFeedbackDrawerOpen}
        onClose={() => dispatch({type: "SET_SHOW_FEEDBACK_DRAWER", payload: false})}
      />
      <ProjectPreviewModal
        isOpen={state.isPreviewModalOpen}
        onClose={handleClosePreviewModal}
        projectId={state.previewProjectId}
      />

      <DeleteProjectAlertDialog
        isOpen={isAlertDialogOpen}
        onClose={onAlertDialogClose}
        selectedProjectId={state.selectedProjectId}
        setSelectedProjectId={(id: string | null) => dispatch({type: "SET_SELECTED_PROJECT_ID", payload: id})}
      />

      <UploadToaster uploads={uploads}/>
    </>
  );
};

export default MainPage;
